import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Case } from "../../types/case";
import { deleteCase, fetchCases, patchCaseData } from "../../services/httpClient";
import { Button, Card, Col, Container, Dropdown, DropdownButton, InputGroup, Row, Spinner, Stack } from "react-bootstrap";
import SearchBar from "../ui/SearchBar";
import PageTitle from "../ui/PageTitle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faClose, faFilter } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "react-tooltip";
import SelectObjectTypeahead from "../ui/SelectObjectTypeahead";
import SelectObject from "../ui/SelectObject";

interface CaseTabState {
  cases: Case[];
  setCases: any;
  casesFiltered: Case[];
  setCasesFiltered: any;
  cmUsers: any[];
  type: string;
  getCases: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  limitedAccess: boolean;
}

const CaseTab = ({
  cases,
  setCases,
  casesFiltered,
  setCasesFiltered,
  cmUsers,
  type,
  getCases,
  setShowToast,
  setToastText,
  setToastBody,
  limitedAccess,
}: CaseTabState) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const [caseToDelete, setCaseToDelete] = useState<Case>();
  const [searchText, setSearchText] = useState<string>("");
  const [isFilterAdded, setIsFilterAdded] = useState<boolean>(false);
  const [caseFilter, setCaseFilter] = useState<string>("");
  const [selectedCMs, setSelectedCMs] = useState<string[]>([]);
  const [isReassignDisabled, setIsReassignDisabled] = useState<boolean>(true);
  const [isReassign, setIsReassign] = useState<boolean>(false);
  const [reassignType, setReassignType] = useState<string>("");
  const [reassignCM, setReassignCM] = useState<string>("0");

  const searchRef = useRef<HTMLInputElement>(null);

  let navigate = useNavigate();

  const newCase = (e: any) => {
    e.preventDefault();
    navigate(`/cases/case/create/${type === "All" ? "Active" : type}`);
  };

  const editCase = (e: any) => {
    navigate(`/cases/case/${e}`);
  };

  const onClickDelete = (e: any) => {
    setIsDeleting(true);
    setCaseToDelete(e);
  };

  const onClickDeleteFinal = () => {
    setIsLoading(true);
    setIsDeleting(false);
    const caseName: string = caseToDelete?.name ? caseToDelete.name : "";

    deleteCase(caseToDelete?.id)
      .then((res: any) => {
        setIsDeleting(false);
        setCaseToDelete(undefined);
        fetchCases()
          .then((res: any) => {
            const cases = res[0].data;
            setCases(cases);
            setShowToast(true);
            setToastText("Case Deleted");
            setToastBody(caseName);
            setIsLoading(false);
          })
          .catch((err: any) => {
            console.log(err);
            setIsLoading(false);
          });
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setCaseToDelete(undefined);
  };

  const onPerformSearchLogic = (text: string) => {
    clearFilters();
    const lowerText = text.toLowerCase();

    var myFiltered: any;
    var myCases = cases;

    //filter by name of case
    myFiltered = myCases?.filter((item: any) => {
      return item.name.toLowerCase().match(lowerText);
    });
    if (myFiltered.length > 0) {
      setIsReassignDisabled(false);
    } else {
      setIsReassignDisabled(true);
    }
    setCasesFiltered(myFiltered);
  };

  const onClickFilterCases = (type: string) => {
    setSearchText("");

    if (type === caseFilter) {
      clearFilters();
      return;
    }
    setCaseFilter(type);
  };

  const onClickFilterBy = (values: any) => {
    var selected: any;
    var filtered;
    var cmIDs: any = [];

    setSelectedCMs(values);
    selected = cmUsers.filter((c) => values.includes(c.key));
    if (selected.length > 0) {
      setSearchText("");
      setIsFilterAdded(true);

      cmIDs = [];
      selected.forEach((selectedCM: any) => {
        cmIDs.push(selectedCM.id);
      });
    }

    switch (caseFilter) {
      case "primaryCM":
        if (selected.length > 0) {
          filtered = cases.filter((c: any) => cmIDs.includes(c.primaryCM?.user_id));
          if (filtered.length > 0) {
            setIsReassignDisabled(false);
          } else {
            setIsReassignDisabled(true);
          }
          setCasesFiltered(filtered);
        } else {
          clearFilters();
        }
        break;
      case "secondaryCM":
        if (selected.length > 0) {
          filtered = cases.filter((c: any) => cmIDs.includes(c.secondaryCM?.user_id));
          if (filtered.length > 0) {
            setIsReassignDisabled(false);
          } else {
            setIsReassignDisabled(true);
          }
          setCasesFiltered(filtered);
        } else {
          clearFilters();
        }
        break;
    }
  };

  const clearFilters = () => {
    setIsFilterAdded(false);
    setCaseFilter("");
    setSelectedCMs([]);
    setCasesFiltered(cases);
    setIsReassignDisabled(true);
  };

  const updateReassignCM = (e: any) => {
    setReassignCM(e.target.value);
  };

  const onClickReassign = (type: string) => {
    setIsReassign(true);
    setReassignType(type);
  };

  const onClickReassignFinal = () => {
    setIsLoading(true);
    casesFiltered.forEach(async (caseItem: any) => {
      await reassignCaseManager(caseItem);
    });

    setTimeout(() => {
      getCases();
    }, 2000);

    setIsReassign(false);
    setReassignType("");
    setReassignCM("0");
    clearFilters();
    setShowToast(true);
    setToastText("Cases Reassigned");
    setToastBody(casesFiltered.length + " case(s)");
  };

  const reassignCaseManager = async (caseItem: any) => {
    var updatedCase: any = {};
    if (reassignType === "Primary Case Manager") {
      updatedCase.primaryCM = reassignCM;
    } else if (reassignType === "Secondary Case Manager") {
      updatedCase.secondaryCM = reassignCM;
    }

    patchCaseData(updatedCase, caseItem.id).catch((err: any) => {
      console.log(err);
    });
  };

  const onClickReassignCancel = () => {
    setIsReassign(false);
    setReassignType("");
    setReassignCM("0");
  };

  const renderReassign = () => {
    return (
      <Stack direction="horizontal" gap={3}>
        <span className="card-subtitle h6">
          Reassign {reassignType} for {casesFiltered.length} case(s) to
        </span>
        <Col>
          <SelectObject label={reassignType} value={reassignCM} options={cmUsers} onUpdate={(e: any) => updateReassignCM(e)} />
        </Col>
        <Button className="app-danger-color button" disabled={reassignCM === "0" ? true : false} onClick={() => onClickReassignFinal()}>
          Confirm Reassign
        </Button>
        <div className="vr" />
        <Button className="app-primary-color button" onClick={() => onClickReassignCancel()}>
          Cancel
        </Button>
      </Stack>
    );
  };

  const renderDelete = () => {
    return (
      <Card className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }}>
              <Card.Subtitle>Are you sure you want to delete {caseToDelete?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal()}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickCancelDelete()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <Row className="mb-5">
      <Col>
        <Stack gap={3}>
          {isLoading ? (
            <Container className="div-main">
              <Button size="sm" className="spinner-button spinner-button-main">
                <Spinner animation="border" style={{ color: "#F6893D" }} />
              </Button>
            </Container>
          ) : (
            <>
              <PageTitle title={type + " Cases"} type="section" buttonTitle="Create Case" onButtonClick={newCase} />
              <Row className="mb-1">
                <Col xs={12}>
                  <InputGroup>
                    <Col>
                      <SearchBar
                        cssClass="search"
                        placeholder={"Search " + type + " Cases"}
                        searchText={searchText}
                        setSearchText={setSearchText}
                        searchLogic={onPerformSearchLogic}
                        externalRef={searchRef}
                      />
                    </Col>
                    <div data-tooltip-id="tooltip-filter" data-tooltip-content={"Filter"}>
                      <DropdownButton
                        id={isFilterAdded ? "dropdown-filter-button-active" : "dropdown-filter-button"}
                        title={<FontAwesomeIcon icon={faFilter} />}
                      >
                        <Dropdown.Item
                          href="#/action-1"
                          className={caseFilter === "primaryCM" ? "app-tertiary-color-inverse" : ""}
                          onClick={() => onClickFilterCases("primaryCM")}
                        >
                          {`Primary Case Manager`}
                          {caseFilter === "primaryCM" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                        </Dropdown.Item>
                        <Dropdown.Item
                          href="#/action-2"
                          className={caseFilter === "secondaryCM" ? "app-tertiary-color-inverse" : ""}
                          onClick={() => onClickFilterCases("secondaryCM")}
                        >
                          {`Secondary Case Manager`}
                          {caseFilter === "secondaryCM" ? <FontAwesomeIcon icon={faClose} className="ms-2" /> : ""}
                        </Dropdown.Item>
                      </DropdownButton>
                    </div>
                    <Tooltip id="tooltip-filter" place="top" style={{ padding: "5px 10px", zIndex: "100" }} />
                  </InputGroup>
                </Col>
              </Row>
              {caseFilter && (
                <Row className="my-2">
                  {isReassign ? (
                    renderReassign()
                  ) : (
                    <>
                      <Col></Col>
                      <Col xs={8}>
                        <Stack direction="horizontal" gap={3}>
                          {caseFilter === "primaryCM" && (
                            <SelectObjectTypeahead
                              label="Primary Case Manager"
                              value={selectedCMs}
                              options={cmUsers}
                              onUpdate={(e: any) => onClickFilterBy(e)}
                              multiSelect={true}
                              focused={true}
                              cssClasses="w-100"
                            />
                          )}
                          {caseFilter === "secondaryCM" && (
                            <SelectObjectTypeahead
                              label="Secondary Case Manager"
                              value={selectedCMs}
                              options={cmUsers}
                              onUpdate={(e: any) => onClickFilterBy(e)}
                              multiSelect={true}
                              focused={true}
                              cssClasses="w-100"
                            />
                          )}
                          {/* <Button
                            key={casesFiltered.length}
                            className="app-tertiary-color button"
                            disabled={isReassignDisabled}
                            onClick={() => onClickReassign(caseFilter === "primaryCM" ? "Primary Case Manager" : "Secondary Case Manager")}
                          >
                            Reassign
                          </Button> */}
                        </Stack>
                      </Col>
                      <Col></Col>
                    </>
                  )}
                </Row>
              )}
              {casesFiltered.length === 0 && (
                <Stack direction="horizontal" gap={2} className="mt-2">
                  <p className="mb-0 me-1">{`No ${type !== "All" ? type : ""} Case Records.`}</p>
                  {!isFilterAdded && (
                    <a href="cases/case/create" className={"quick-link cursor-pointer"} onClick={(e: any) => newCase(e)}>
                      Create Case
                    </a>
                  )}
                </Stack>
              )}
              {isDeleting && caseToDelete
                ? renderDelete()
                : casesFiltered?.map((item: any) => (
                    <Card key={item?.id} className="bg-light">
                      <Card.Body>
                        <Stack direction="horizontal" gap={3}>
                          <Stack className="cursor-pointer" onClick={(e) => editCase(item.id)}>
                            {item.disabled_at ? (
                              <Card.Subtitle className="text-muted">{item.name}</Card.Subtitle>
                            ) : (
                              <Card.Subtitle>{item.name}</Card.Subtitle>
                            )}
                            <Card.Subtitle className="text-muted mt-1">
                              {item.primaryCM === null && selectedCMs.length === 0 && "Case Manager Unassigned"}
                              {caseFilter === "primaryCM" && item.primaryCM !== null && `Primary Case Manager`}
                              {caseFilter === "secondaryCM" && item.secondaryCM !== null && `Secondary Case Manager`}
                            </Card.Subtitle>
                          </Stack>
                          <Button className="app-secondary-color button" onClick={(e) => editCase(item.id)}>
                            Edit
                          </Button>
                          {!limitedAccess && (
                            <>
                              <div className="vr" />
                              <Button className="app-danger-color button" onClick={(e) => onClickDelete(item)}>
                                Delete
                              </Button>
                            </>
                          )}
                        </Stack>
                      </Card.Body>
                    </Card>
                  ))}
            </>
          )}
        </Stack>
      </Col>
    </Row>
  );
};

export default CaseTab;
